// TODO (Core): Move this type to a package
import {CookieConfig} from 'types/Cookie'

import {getCookie, setCookie} from '../../utils/cookies'
import {isBrowser} from '../../utils/environment'
import {sapiLabelCookieConfig} from './cookieConfig'
import {generateSapiLabelFromBrowserContext} from './generateBrowserSapiLabel'

export {generateSapiLabelFromBrowserContext} from './generateBrowserSapiLabel'

const getCookieOptions = (cookieConfig: CookieConfig) => {
  const {secure, sameSite, maxAge: expires, domain} = cookieConfig.options
  return {
    expires,
    secure,
    sameSite,
    domain
  }
}

export const getPersistedSapiLabel = () => getCookie(sapiLabelCookieConfig.name)

/**
 * This function returns existing SAPI label or generates and persists a new one using current location url.
 * It can be used in cases of navigation to non-search web app pages from external (white labeled) websites.
 * @param partnerId - Optional parameters for label generation
 * @returns string - SAPI label
 */
export const getPersistedSapiLabelWithFallback = (partnerId?: string) => {
  // The server can't read the cookie or persist a new one
  if (!isBrowser) return ''

  const existingLabel = getPersistedSapiLabel()

  if (existingLabel) {
    return existingLabel
  }

  const fallbackLabel =
    generateSapiLabelFromBrowserContext(window.location.search, partnerId) || ''

  persistSapiLabel(fallbackLabel)

  return fallbackLabel
}

/**
 * Checks utm_source at the top level, then src inside the nested label param.
 * @returns The raw traffic source, or undefined if none found.
 */
const getTrafficSourceFromQuery = (query: string): string | undefined => {
  const params = new URLSearchParams(query)
  const utmSource = params.get('utm_source')
  if (utmSource) return utmSource

  const src = new URLSearchParams(params.get('label') ?? '').get('src')
  return src || undefined
}

/**
 * Extract the traffic source from the persisted SAPI label cookie
 * for use as the AppsFlyer media source (pid).
 * @returns The traffic source (lowercased), or 'Direct' if none found.
 */
export const getPersistedMediaSource = (): string =>
  getTrafficSourceFromQuery(getPersistedSapiLabel() ?? '')?.toLowerCase() ??
  'Direct'

/**
 * Raw traffic source for the deep link `utm_source` (sub9), not lowercased.
 * Reads the landing query string first, which is available on the first render,
 * then falls back to the persisted SAPI label cookie.
 * @returns The traffic source, or undefined if none found.
 */
export const getUtmSource = (landingQueryString = ''): string | undefined =>
  getTrafficSourceFromQuery(landingQueryString) ??
  getTrafficSourceFromQuery(getPersistedSapiLabel() ?? '')

/**
 * This function persists the already generated SAPI label to a cookie.
 * @param sapiLabel - SAPI label to be persisted with the proper configuration for this cookie
 */
export const persistSapiLabel = (sapiLabel: string) =>
  setCookie(
    sapiLabelCookieConfig.name,
    sapiLabel,
    getCookieOptions(sapiLabelCookieConfig)
  )
